import React from 'react';
import { useProfilePhoto } from '../../contexts/ProfilePhotoContext';
import FormHeader from '../personal-info/FormHeader';
import {
  UploadIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Trash2Icon,
  UserIcon,
  CheckIcon,
} from '../common/IconsComponent';
import SpinnerOverlay from '../ui/SpinnerOverlay';

const ProfilePhotoSection = React.forwardRef(({ active }, ref) => {
  const {
    photos,
    selectedPhotoIndex,
    handleUploadClick,
    handleFileChange,
    handleDelete,
    handleNext,
    handlePrevious,
    handleApplyPhoto,
    fileInputRef,
    setSelectedPhotoIndex,
    loading,
  } = useProfilePhoto();

  if (!active) return null;

  const selected = photos[selectedPhotoIndex];

  return (
    <div dir="rtl" ref={ref} id="profile-photo-section" className="scroll-mt-24">
      <div className="relative bg-white rounded-2xl shadow-md border border-indigo-50 p-4 sm:p-6 lg:p-8 mb-4 sm:mb-6">
        {loading && (
          <SpinnerOverlay
            variant="glow"
            text="נא להמתין"
            dark={false}
            blur={false}
          />
        )}

        <FormHeader
          title="בחירת תמונת פרופיל"
          buttonText="החל תמונה"
          disabled={photos.length === 0 || loading}
          type="button"
          onClick={handleApplyPhoto}
        />

        <div
          onClick={handleUploadClick}
          className="border-2 border-dashed border-gray-300 rounded-xl p-5 sm:p-6 mb-5 sm:mb-6 text-center cursor-pointer hover:border-blue-400 transition-colors"
        >
          <UploadIcon size={28} className="mx-auto mb-2 text-gray-400" />
          <p className="text-gray-700 font-medium text-sm sm:text-base">
            גרור תמונות לכאן או לחץ להעלאה
          </p>
          <p className="text-gray-400 text-xs sm:text-sm mt-1">JPG, PNG • מקסימום 5MB</p>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
            accept="image/*"
          />
        </div>

        <div className="flex flex-row-reverse gap-2 sm:gap-3 overflow-x-auto py-2 px-2 mb-5 sm:mb-6 bg-gray-50 rounded-xl">
          {photos.map((photo, index) => (
            <div
              key={photo.id}
              onClick={() => setSelectedPhotoIndex(index)}
              className={`w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden cursor-pointer flex-shrink-0 relative transition-all ${
                selectedPhotoIndex === index
                  ? 'ring-2 ring-blue-500'
                  : 'opacity-80 hover:opacity-100'
              }`}
            >
              <img
                src={photo.src}
                alt={`תמונה ${index + 1}`}
                className="w-full h-full object-cover"
              />
              {selectedPhotoIndex === index && (
                <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center">
                  <CheckIcon size={20} className="text-white" />
                </div>
              )}
            </div>
          ))}

          {Array.from({ length: Math.max(0, 5 - photos.length) }).map((_, i) => (
            <div
              key={`empty-${i}`}
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg border-2 border-dashed border-gray-300 flex-shrink-0 flex items-center justify-center bg-white"
            >
              <UploadIcon size={16} className="text-gray-300" />
            </div>
          ))}
        </div>

        <div className="relative bg-gray-100 rounded-xl overflow-hidden h-48 sm:h-64">
          {selected ? (
            <>
              <div className="absolute inset-0 flex items-center justify-center">
                <img
                  src={selected.src}
                  alt="תצוגה מקדימה"
                  className="max-h-full max-w-full object-contain"
                />
              </div>

              <button
                type="button"
                onClick={() => handleDelete(selected.id)}
                className="absolute top-3 right-3 bg-black/50 hover:bg-black/70 p-1.5 sm:p-2 rounded-full text-white transition-colors"
                aria-label="מחק תמונה"
              >
                <Trash2Icon size={16} />
              </button>

              {photos.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={handlePrevious}
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-1.5 sm:p-2 rounded-full shadow-md transition-colors"
                    aria-label="הקודם"
                  >
                    <ChevronLeftIcon size={20} />
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-1.5 sm:p-2 rounded-full shadow-md transition-colors"
                    aria-label="הבא"
                  >
                    <ChevronRightIcon size={20} />
                  </button>
                </>
              )}
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 gap-2">
              <UserIcon size={40} />
              <span className="text-sm">לא הועלו תמונות</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default ProfilePhotoSection;